import React, {
  Dispatch,
  SetStateAction,
  useState,
} from 'react';
import { useReCaptcha } from 'next-recaptcha-v3';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIntl } from 'react-intl';

import { globalMessages } from '@/core/messages/global.messages';
import { TextField } from '@/core/components/TextField';
import { Textarea } from '@/core/components/Textarea';
import { ButtonVariant } from '@/core/components/Button/Button.types';
import { SendIcon } from '@/core/components/Icons/Icons.stories';
import { contactMessages } from '@/core/messages/contact.messages';

import { api } from '../api';
import {
  ErrorBar,
  FormWrapper,
  InputContainer,
  RowContainer,
  StyledButton,
  Wrapper,
} from './ContactForm.styles';

export type ContactDataType = {
  fullName: string;
  email: string;
  subject: string;
  message: string;
};

type Props = {
  setIsFormSubmitted: Dispatch<SetStateAction<boolean>>;
};

export const ContactForm = ({ setIsFormSubmitted }: Props) => {
  const { formatMessage } = useIntl();
  const schema = yup.object().shape({
    email: yup
      .string()
      .email(formatMessage(globalMessages.emailRequired))
      .required(formatMessage(globalMessages.fieldRequired)),
    fullName: yup
      .string()
      .max(100, formatMessage(globalMessages.fieldMessageTooLong))
      .required(formatMessage(globalMessages.fieldRequired)),
    message: yup.string().required(formatMessage(globalMessages.fieldRequired)),
    subject: yup.string()
      .max(100, formatMessage(globalMessages.fieldMessageTooLong))
      .required(formatMessage(globalMessages.fieldRequired)),
  });
  const { executeRecaptcha } = useReCaptcha();
  const [
    isError,
    setIsError,
  ] = useState(false);
  const [
    isLoading,
    setIsLoading,
  ] = useState(false);
  const methods = useForm<ContactDataType>({
    mode: 'onBlur',
    resolver: yupResolver(schema),
  });
  const {
    reset,
    formState: {
      errors,
      isDirty,
    },
    handleSubmit,
  } = methods;

  const onSubmit = async (form: ContactDataType) => {
    setIsLoading(true);
    setIsError(false);

    try {
      const token = await executeRecaptcha('onSubmit');

      await api.authorize(token);
      await api.postMail(form);
      reset();
      setIsFormSubmitted(true);
    } catch (e) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Wrapper>
          <FormWrapper>
            <RowContainer>
              <TextField
                name={formatMessage(contactMessages.fullNameTextField)}
                id="fullName"
                placeholder={formatMessage(contactMessages.fullNameTextField)}
              />
              <TextField
                name={formatMessage(contactMessages.emailTextField)}
                id="email"
                placeholder={formatMessage(contactMessages.emailTextField)}
              />
            </RowContainer>
            <InputContainer>
              <TextField
                name={formatMessage(contactMessages.subjectTextField)}
                id="subject"
                placeholder={formatMessage(contactMessages.subjectTextField)}
              />
            </InputContainer>
            <InputContainer>
              <Textarea
                name={formatMessage(contactMessages.yourMessageTextFieldLabel)}
                id="message"
                characterLimit={2000}
                placeholder={formatMessage(contactMessages.yourMessageTextFieldPlaceholder)}
              />
            </InputContainer>
            <StyledButton
              type="submit"
              variant={ButtonVariant.PRIMARY_INVERTED}
              label={formatMessage(contactMessages.buttonSendMessage)}
              endIcon={<SendIcon />}
              disabled={isLoading || !isDirty || !!Object.keys(errors).length}
            />
          </FormWrapper>
        </Wrapper>
      </form>
      {isError && (
        <ErrorBar>
          {formatMessage(contactMessages.messageDeliveryError)}
        </ErrorBar>
      )}
    </FormProvider>
  );
};
